import { FeatureCollection, MultiLineString } from "@turf/helpers";
import { LaneTemperatureProfileRequestModel } from "api/lokiRequestModels/lane/LaneTemperatureProfileRequestModel";
import { LaneTemperatureStatisticsRequestModel } from "api/lokiRequestModels/lane/LaneTemperatureStatisticsRequestModel";
import { LaneTripStatisticsRequestModel } from "api/lokiRequestModels/lane/LaneTripStatisticsRequestModel";
import { RouteAggregationsRequestModel } from "api/lokiRequestModels/route/RouteAggregationsRequestModel";
import { RouteDetailsRequestModel } from "api/lokiRequestModels/route/RouteDetailsRequestModel";
import { services } from "api/serviceConfig";
import { convertRoutesToGeojson } from "helpers/mapHelpers";
import { makeAutoObservable } from "mobx";
import { ProductExcursions } from "models/laneExcursion/ProductExcursions";
import { LaneTempProfile } from "models/laneTempProfile/LaneTempProfile";
import { RouteAggregations } from "models/routeAggregations/RouteAggregations";
import { RouteDetails } from "models/routeDetails/RouteDetails";
import { RouteTempExcursionAggregationsRequestModel } from "../api/lokiRequestModels/route/RouteTempExcursionAggregationsRequestModel";
import { ShipmentDetailsRequestModel } from "../api/lokiRequestModels/shipments/ShipmentDetailsRequestModel";
import { ShipmentDetails } from "../models/shipmentDetails/ShipmentDetails";

export class LaneViewStore {
    currentRoutePage = 1;
    totalLoadedRoutePages = 1;
    lastRoutePage = 0;
    routeNextToken: string | undefined = undefined;

    currentShipmentPage = 1;
    totalLoadedShipmentPages = 1;
    lastShipmentPage = 0;
    shipmentNextToken: string | undefined = undefined;

    // getLaneTripStatistics
    minTrip: number = 0.0;
    maxTrip: number = 0.0;
    meanTrip: number = 0.0;
    stdDevTrip: number = 0.0;

    // getLaneTemperatureStatistics
    minTempF: number | null = null;
    maxTempF: number | null = null;
    meanTempF: number | null = null;
    stdDevTempF: number | null = null;
    unitFahrenheit: string = "°F";

    minTempC: number = 0;
    maxTempC: number = 0;
    meanTempC: number = 0;
    stdDevTempC: number = 0;
    unitCelsius: string = "°C";

    // getLaneTemperatureProfile
    laneTempProfile: LaneTempProfile[] = [];

    laneTempProfileCurrent: LaneTempProfile[] = [];

    // listRouteDetails
    routePages: RouteDetails[][] = [];

    // listShipmentDetails
    shipmentPages: ShipmentDetails[][] = [];

    // getRouteAggregations
    routeAggregations: RouteAggregations[] = [];

    // getRouteTempExcursionAggregations
    productExcursionsList: ProductExcursions[] = [];

    loadingAggregations = false;
    loadingStatistics = false;
    loadingTemperatureStatistics = false;
    loadingTemperatureExcursions = false;
    loadingRoutes = false;
    loadingLaneTemperatureProfile = false;
    loadingShipments = false;

    sortRoute: string = "riskLevel";
    directionRoute: "ASC" | "DESC" = "DESC";

    sortShipment: string = "riskLevel";
    directionShipment: "ASC" | "DESC" = "DESC";

    constructor() {
        makeAutoObservable(this);
    }

    get isLastRoutePage() {
        return this.currentRoutePage === this.lastRoutePage;
    }

    get isLastShipmentPage() {
        return this.currentShipmentPage === this.lastShipmentPage;
    }

    get routesGeojson(): FeatureCollection<MultiLineString> {
        if (this.routePages.length > 0) {
            return convertRoutesToGeojson(
                this.routePages[this.currentRoutePage - 1]
            );
        }

        return { type: "FeatureCollection", features: [] };
    }

    loadLaneTripStatistics = async (
        request: LaneTripStatisticsRequestModel
    ) => {
        this.loadingStatistics = true;

        try {
            const response = await services.Lane.getLaneTripStatistics(request);
            this.minTrip = response.data.minTripDuration;
            this.maxTrip = response.data.maxTripDuration;
            this.meanTrip = response.data.meanTripDuration;
            this.stdDevTrip = response.data.tripDurationStandardDeviation;
        } catch (error) {
            console.log(error);
        } finally {
            this.loadingStatistics = false;
        }
    };

    loadLaneTemperatureStatistics = async (
        request: LaneTemperatureStatisticsRequestModel
    ) => {
        this.loadingTemperatureStatistics = true;

        try {
            const response = await services.Lane.getLaneTemperatureStatistics(
                request
            );
            this.minTempF = response.data.minTemperature;
            this.maxTempF = response.data.maxTemperature;
            this.meanTempF = response.data.meanTemperature;
            this.stdDevTempF = response.data.temperatureStandardDeviation;

            this.minTempC = (this.minTempF - 32) / 1.8;
            this.maxTempC = (this.maxTempF - 32) / 1.8;
            this.meanTempC = (this.meanTempF - 32) / 1.8;
            this.stdDevTempC = this.stdDevTempF / 1.8;
        } catch (error) {
            console.log(error);
        } finally {
            this.loadingTemperatureStatistics = false;
        }
    };

    loadLaneTemperatureProfile = async (
        request: LaneTemperatureProfileRequestModel
    ) => {
        this.laneTempProfile = [];
        this.loadingLaneTemperatureProfile = true;
        try {
            const response = await services.Lane.getLaneTemperatureProfile(
                request
            );
            this.laneTempProfile = response.data.laneTempProfile;
        } catch (error) {
            console.log(error);
        } finally {
            this.loadingLaneTemperatureProfile = false;
        }
    };

    loadFirstRoutesPage = async (request: RouteDetailsRequestModel) => {
        this.loadingRoutes = true;

        try {
            const response = await services.Route.listRouteDetails(request);
            this.clearRoutePagination();
            if (response.data.routeDetails.length > 0) {
                this.routePages.push(response.data.routeDetails);
            }
            if (!response.data.nextToken) {
                this.lastRoutePage = this.currentRoutePage;
            } else {
                this.routeNextToken = response.data.nextToken;
            }
        } catch (error) {
            this.lastRoutePage = this.currentRoutePage;
            console.log(error);
        } finally {
            this.loadingRoutes = false;
        }
    };

    moveToNextRoutesPage = async (request: RouteDetailsRequestModel) => {
        this.loadingRoutes = true;
        try {
            if (this.totalLoadedRoutePages === this.currentRoutePage) {
                const response = await services.Route.listRouteDetails(request);
                this.routePages.push(response.data.routeDetails);
                if (!response.data.nextToken) {
                    this.lastRoutePage = this.currentRoutePage + 1;
                } else {
                    this.routeNextToken = response.data.nextToken;
                    this.totalLoadedRoutePages++;
                }
            }
            this.currentRoutePage++;
        } catch (error) {
            console.log(error);
        } finally {
            this.loadingRoutes = false;
        }
    };

    moveToPreviousRoutePage = () => {
        this.currentRoutePage--;
    };

    moveToPreviousShipmentsPage = () => {
        this.currentShipmentPage--;
    };

    loadRouteAggregations = async (request: RouteAggregationsRequestModel) => {
        this.loadingAggregations = true;

        try {
            const response = await services.Route.getRouteAggregations(request);
            this.routeAggregations = response.data.routeAggregations || [];
        } catch (error) {
            console.log(error);
        } finally {
            this.loadingAggregations = false;
        }
    };

    loadProductExcursionsList = async (
        request: RouteTempExcursionAggregationsRequestModel
    ) => {
        this.loadingTemperatureExcursions = true;

        try {
            const response =
                await services.Route.getRouteTempExcursionAggregations(request);
            this.productExcursionsList =
                response.data.productExcursionsList || [];
        } catch (error) {
            console.log(error);
        } finally {
            this.loadingTemperatureExcursions = false;
        }
    };

    loadFirstShipmentsPage = async (request: ShipmentDetailsRequestModel) => {
        this.loadingShipments = true;

        try {
            const response = await services.Shipment.listShipmentsDetails(
                request
            );
            this.clearShipmentPagination();
            if (response.data.shipmentDetails.length > 0) {
                this.shipmentPages.push(response.data.shipmentDetails);
            }
            if (!response.data.nextToken) {
                this.lastShipmentPage = this.currentShipmentPage;
            } else {
                this.shipmentNextToken = response.data.nextToken;
            }
        } catch (error) {
            this.lastShipmentPage = this.currentShipmentPage;
            console.log(error);
        } finally {
            this.loadingShipments = false;
        }
    };

    moveToNextShipmentPage = async (request: ShipmentDetailsRequestModel) => {
        this.loadingShipments = true;
        try {
            if (this.totalLoadedShipmentPages === this.currentShipmentPage) {
                const response = await services.Shipment.listShipmentsDetails(
                    request
                );
                this.shipmentPages.push(response.data.shipmentDetails);
                if (!response.data.nextToken) {
                    this.lastShipmentPage = this.currentShipmentPage + 1;
                } else {
                    this.shipmentNextToken = response.data.nextToken;
                    this.totalLoadedShipmentPages++;
                }
            }
            this.currentShipmentPage++;
        } catch (error) {
            console.log(error);
        } finally {
            this.loadingShipments = false;
        }
    };

    clearRoutePagination = () => {
        this.routePages = [];
        this.currentRoutePage = 1;
        this.totalLoadedRoutePages = 1;
        this.lastRoutePage = 0;
        this.routeNextToken = undefined;
    };

    clearShipmentPagination = () => {
        this.shipmentPages = [];
        this.currentShipmentPage = 1;
        this.totalLoadedShipmentPages = 1;
        this.lastShipmentPage = 0;
        this.shipmentNextToken = undefined;
    };

    setRouteSortProperty = (property: string) => {
        this.sortRoute = property;
    };

    setRouteDirection = (direction: "ASC" | "DESC") => {
        this.directionRoute = direction;
    };

    setShipmentSortProperty = (property: string) => {
        this.sortShipment = property;
    };

    setShipmentDirection = (direction: "ASC" | "DESC") => {
        this.directionShipment = direction;
    };

    resetRouteFilters = () => {
        this.sortRoute = "riskLevel";
        this.directionRoute = "DESC";
    };

    resetShipmentsFilters = () => {
        this.sortShipment = "riskLevel";
        this.directionShipment = "DESC";
    };
}
